import {
  Money,
  MoneySchema,
  moneyAdd,
  moneyFromMinorAmount,
} from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { MercuryModelMode } from "../conversion/mercury-model";
import { CorrelatedCarrierServiceSchema } from "../fulfillments/fulfillment-carriers-and-services";
import {
  DynamicRateRounding,
  RateTableType,
} from "../integration/redo-in-shipping/shipping-rate-table";
import { getParcelsForCheckoutFromOmsAutomationResultSchema } from "./checkout-oms-automation-utils";

/**
 * Shopify's carrier service API sends us the shop's base currency, not the
 * customer's browsing currency. We guess the customer's currency from their
 * destination country and pre-convert the landed cost breakdown into that
 * currency. When the guess is correct, the checkout UI can use these values
 * directly — avoiding a second conversion that would introduce rounding
 * mismatches between the breakdown and the total price Shopify displays.
 */
const customerCurrencyEstimateSchema_ = z.object({
  duties: z.number(), // minor units in guessed customer currency
  taxes: z.number(), // minor units in guessed customer currency
  fees: z.number(), // minor units in guessed customer currency
  currency: createEnum(Currency, { enumName: "Currency" }),
  exchangeRate: z.number(), // shop currency → guessed customer currency
});
export const customerCurrencyEstimateSchema: z.ZodType<CustomerCurrencyEstimate> =
  customerCurrencyEstimateSchema_;
export interface CustomerCurrencyEstimate
  extends z.infer<typeof customerCurrencyEstimateSchema_> {}

const landedCostDataSchema_ = z.object({
  zonosQuoteId: z.string(),
  duties: z.number(), // minor units, shop currency
  taxes: z.number(), // minor units, shop currency
  fees: z.number(), // minor units, shop currency
  currency: createEnum(Currency, { enumName: "Currency" }),
  /** Present only when the customer's guessed currency differs from the shop's. */
  customerCurrencyEstimate: customerCurrencyEstimateSchema.optional(),
});
export const landedCostDataSchema: z.ZodType<LandedCostData> =
  landedCostDataSchema_;
export interface LandedCostData extends z.infer<typeof landedCostDataSchema_> {}

/**
 * The duties + taxes the customer paid at checkout under Redo's landed-cost
 * guarantee, summed across the order's selected shipping options. Amounts come
 * back in whatever currency we stored: the customer-currency estimate when one
 * was saved (so it matches the email's presentment currency), otherwise the
 * shop currency. No currency conversion. Returns null when no option carries
 * landed cost or the totals are zero.
 */
export function getCustomerPaidLandedCost(
  options:
    | readonly { landedCostData?: LandedCostData | null }[]
    | null
    | undefined,
): { duties: Money; taxes: Money } | null {
  const [first, ...rest] = (options ?? [])
    .map((option) => option.landedCostData)
    .filter((lcd): lcd is LandedCostData => lcd != null)
    .map(customerFacingAmounts);
  if (!first) {
    return null;
  }
  // One selected option per order in practice; only sum entries sharing the
  // first entry's currency rather than risk adding across currencies.
  const { currency } = first;
  const inCurrency = [first, ...rest].filter((lc) => lc.currency === currency);
  const duties = sumMinorAmounts(
    inCurrency.map((lc) => lc.duties),
    currency,
  );
  const taxes = sumMinorAmounts(
    inCurrency.map((lc) => lc.taxes),
    currency,
  );
  if (duties.amount <= 0 && taxes.amount <= 0) {
    return null;
  }
  return { duties, taxes };
}

function customerFacingAmounts(lcd: LandedCostData): {
  duties: number;
  taxes: number;
  currency: Currency;
} {
  const estimate = lcd.customerCurrencyEstimate;
  return estimate
    ? {
        duties: estimate.duties,
        taxes: estimate.taxes,
        currency: estimate.currency,
      }
    : { duties: lcd.duties, taxes: lcd.taxes, currency: lcd.currency };
}

function sumMinorAmounts(
  minorAmounts: readonly number[],
  currency: Currency,
): Money {
  return minorAmounts.reduce(
    (sum, minor) => moneyAdd(sum, moneyFromMinorAmount(minor, currency)),
    { amount: 0, currency },
  );
}

const baseRedoShippingOptionSchema = z.object({
  provider: z.literal("redo"),
  rateTableId: zExt.objectId(),
  serviceCodeSentToCheckoutWhenMetafieldsUsed: z.string().optional(),
  checkoutTreeNodeId: z.string(),
  checkoutExperienceId: z.string().optional(),
  assignedTreatmentId: z.string().optional(),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
  landedCostData: landedCostDataSchema.optional(),
});

const markupSchema = z.object({
  markupPercentage: z.string().optional(),
  markupAmount: z.string().optional(),
});

const costBreakdownSchema = z.object({
  costCurrency: createEnum(Currency, { enumName: "Currency" }),
  labelCost: z.string(),
  markup: markupSchema.optional(),
  rounding: createEnum(DynamicRateRounding, {
    enumName: "DynamicRateRounding",
  }).optional(),
});
export interface CostBreakdown extends z.infer<typeof costBreakdownSchema> {}

const dynamicRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.DYNAMIC),
  carrierService: CorrelatedCarrierServiceSchema,
  /**
   * Multiple parcels can cause a rate table to have multiple carrier services
   */
  carrierServices: z.array(CorrelatedCarrierServiceSchema).optional(),
  costBreakdown: costBreakdownSchema,
  promisedDeliveryDate: z.string().optional(),
});

const fixedRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.FIXED),
});

const customRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.CUSTOM),
});

const checkoutShippingOptionSchema_ = z.union([
  dynamicRedoShippingOptionSchema,
  fixedRedoShippingOptionSchema,
  customRedoShippingOptionSchema,
]);
export const checkoutShippingOptionSchema: z.ZodType<CheckoutShippingOption> =
  checkoutShippingOptionSchema_;
export type CheckoutShippingOption = z.infer<
  typeof checkoutShippingOptionSchema_
>;

export const featureValuePairSchema = z.array(
  z.tuple([z.string(), z.number()]),
);
export const optionWeightsSchema = z.record(z.string(), featureValuePairSchema);

const baseMercuryResultSchema = z.object({
  checkoutExperienceId: z.string(),
  checkoutTreeNodeId: z.string(),
  optimalPrices: z.record(z.string(), z.number()),
  labelCostShopMoneyByRateTableId: z.record(z.string(), MoneySchema).nullish(),
});

const mercuryLiteResultSchema_ = baseMercuryResultSchema.extend({
  modelMode: z.literal(MercuryModelMode.LITE_ENABLED),
});
export const mercuryLiteResultSchema: z.ZodType<MercuryLiteResult> =
  mercuryLiteResultSchema_;
export interface MercuryLiteResult
  extends z.infer<typeof mercuryLiteResultSchema_> {}

const mercuryFullResultSchema = baseMercuryResultSchema.extend({
  modelMode: z.union([
    z.literal(MercuryModelMode.ENABLED),
    z.literal(MercuryModelMode.SHADOW),
  ]),
  expectedMargin: z.number(),
  expectedMarginsByRateTable: z.record(z.string(), z.number()),
  priceCombinationsExplored: z.number().nullish(),
  betaWeightsMean: optionWeightsSchema.nullish(),
  betaWeightsStd: optionWeightsSchema.nullish(),
  betaWeightsSampled: optionWeightsSchema.nullish(),
  featuresWithOptimalPrices: featureValuePairSchema.nullish(),
  inferenceTimeMs: z.number(),
});

const mercuryResultSchema_ = z.union([
  mercuryLiteResultSchema_,
  mercuryFullResultSchema,
]);
export const mercuryResultSchema: z.ZodType<MercuryResult> =
  mercuryResultSchema_;
export type MercuryResult = z.infer<typeof mercuryResultSchema_>;

export enum SelectedParcelSource {
  OMS_AUTOMATION = "OMS_AUTOMATION",
  PARCEL_TREE = "PARCEL_TREE",
  DEFAULT = "DEFAULT",
}

const parcelSelectionDecisionInfoSchema = z.union([
  z.object({
    source: z.literal(SelectedParcelSource.OMS_AUTOMATION),
    omsAutomationParcelResult:
      getParcelsForCheckoutFromOmsAutomationResultSchema,
  }),
  z.object({ source: z.literal(SelectedParcelSource.PARCEL_TREE) }),
  z.object({ source: z.literal(SelectedParcelSource.DEFAULT) }),
]);

const selectedParcelInfoSchema = z.object({
  selectedParcelIds: z.array(z.string()),
  parcelSelectionDecisionInfo: parcelSelectionDecisionInfoSchema,
});
export interface SelectedParcelInfo
  extends z.infer<typeof selectedParcelInfoSchema> {}

const checkoutShippingOptionsDocumentSchema_ = z.object({
  _id: zExt.objectId(),
  stableId: z.string().optional(),
  checkoutToken: z.string().optional(),
  team: zExt.objectId(),
  options: z.array(checkoutShippingOptionSchema_),
  checkoutExperienceIdToMercuryModelId: z
    .record(z.string(), z.string().nullish())
    .nullish(),
  mercuryResults: z.array(mercuryResultSchema_).nullish(),
  selectedParcelInfoByRateTableId: z
    .record(z.string(), selectedParcelInfoSchema)
    .nullish(),
  /**
   * True when this document was persisted by the synchronous rates fallback
   * path. Fulfillment-time consumers (notably the
   * `applyCheckoutRatePurchased` Temporal activity) use this to skip applying
   * the synthesized carrier+service to the FulfillmentGroup, letting the OMS
   * pick the carrier naturally from real outbound rates instead.
   */
  fromFallback: z.boolean().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const checkoutShippingOptionsDocumentSchema: z.ZodType<CheckoutShippingOptionsDocument> =
  checkoutShippingOptionsDocumentSchema_;
export interface CheckoutShippingOptionsDocument
  extends z.infer<typeof checkoutShippingOptionsDocumentSchema_> {}

/**
 * Input schema for creating a new document (without timestamps and _id)
 */
const createCheckoutShippingOptionsDocumentSchema_ =
  checkoutShippingOptionsDocumentSchema_.omit({
    _id: true,
    stableId: true,
    createdAt: true,
    updatedAt: true,
  });
export const createCheckoutShippingOptionsDocumentSchema: z.ZodType<CreateCheckoutShippingOptionsDocument> =
  createCheckoutShippingOptionsDocumentSchema_;
export interface CreateCheckoutShippingOptionsDocument
  extends z.infer<typeof createCheckoutShippingOptionsDocumentSchema_> {}
